import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icons, toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { useNotificationCenter } from "react-toastify/addons/use-notification-center";
import {
  selectActiveWinery,
  selectConfigs,
  selectIsLabelsLoaded,
  selectLabels,
  selectNotificationsUnread,
} from "../../../context/selectors";
import useLabels from "../../../hooks/useLabels";
import { Switch } from "../../FormComponents";
import { toggleNotificationsUnreadGrid } from "../../../context/configs/configsSlice";
import { Button, Tooltip } from "../../Generic";
import "./styles/index.scss";
import "../../../components/Header/styles/index.scss";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { ROLES } from "../../../constants/base";
import useAuth from "../../../hooks/useAuth";
import { getOperationById, getOperations, markNotificationAsRead, getProtocolApplications } from "../../../services/utils";
import { socket } from "../../../socket";
import { useNavigate } from "react-router-dom";
import { add as addOp, updateNote, updateOp, removeOp, empty } from "../../../context/operations/operationsSlice";
import pop from "../../../assets/sounds/pop.mp3"
import { format } from "date-fns";
import { addCompletedOp } from "../../../context/completedOperations/completedOperationsSlice";
import { setApplications } from "../../../context/protocols/applicationSlice";
import { getItem } from "../../../services/LocalStorage";
import { Icon } from "../../../components/Icon";


const NOTIFICATION_INVALIDATION_MAP = {
  NEW_OPERATION: ["NEW_OPERATION"],
  UPDATE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  START_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "START_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  COMPLETE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  DELETE_OPERATION: [
    "NEW_OPERATION",
    "UPDATE_OPERATION",
    "COMPLETE_OPERATION",
    "COMPLETE_SAMPLING",
  ],
  APPLY_WORKING_PROTOCOL: ["UPDATE_OPERATION"],
};

const variants = {
  container: {
    open: {
      y: 0,
      opacity: 1,
    },
    closed: {
      y: -10,
      opacity: 0,
    },
  },
  content: {
    open: {
      transition: { staggerChildren: 0.07, delayChildren: 0.2 },
    },
    closed: {
      transition: { staggerChildren: 0.05, staggerDirection: -1 },
    },
  },
  item: {
    open: {
      y: 0,
      opacity: 1,
      transition: {
        y: { stiffness: 1000, velocity: -100 },
      },
    },
    closed: {
      y: 50,
      opacity: 0,
      transition: {
        y: { stiffness: 1000 },
      },
    },
  },
};

const NotificationsCenter = ({ isOpen, setCounter }) => {
  const [getLabel] = useLabels();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeWinery = useSelector(selectActiveWinery);
  const configs = useSelector(selectConfigs);
  const isLabelsLoaded = useSelector(selectIsLabelsLoaded);
  const showUnreadOnly = useSelector(selectNotificationsUnread);

  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const seenNotificationsRef = useRef(new Set());

  const {
    notifications,
    clear,
    markAllAsRead,
    markAsRead,
    remove,
    unreadCount,
    add,
  } = useNotificationCenter({
    filter: (item) => item.data.exclude === false,
  });

  useEffect(() => {
    if (!isLabelsLoaded) return;

    // Attach listener immediately when component mounts
    socket.on("notifications", onNotifications);

    return () => {
      // Cleanup listener on unmount
      socket.off("notifications", onNotifications);
    };
  }, [isLabelsLoaded]); // Only re-run when isLabelsLoaded changes

  const removeOnCondition = (predicate) => {
    notifications
      ?.filter(predicate)
      ?.forEach((n) => remove(n.id));
  };

  const invalidateOldNotifications = (incomingNotification) => {
    const { type, data } = incomingNotification;
    const operationId = data?.operation_id;

    if (!operationId) return;

    const typesToInvalidate = NOTIFICATION_INVALIDATION_MAP[type];
    if (!typesToInvalidate) return;

    removeOnCondition((n) =>
      n?.data?.operation_id === operationId &&
      typesToInvalidate.includes(n?.type)
    );
  };

  const handleNotifications = (notification) => {
    // 1️⃣ deduplica hard (id)
    if (seenNotificationsRef.current.has(notification.id)) return;
    seenNotificationsRef.current.add(notification.id);

    // 2️⃣ invalida notifiche obsolete
    invalidateOldNotifications(notification);

    const isLink =
      notification?.data?.operation_id &&
      notification?.data?.operation_type &&
      // (notification.type === "NOTE", "NEW_OPERATION" || notification.type === "UPDATE_OPERATION")
      ["NOTE", "NEW_OPERATION", "UPDATE_OPERATION", "START_OPERATION", "DELETE_OPERATION", "COMPLETE_SAMPLING"].includes(notification?.type)
        ? () => {
            markAsRead(notification.id);
            navigate(
              `/programming/${notification?.data?.operation_type?.toLowerCase?.()}/${notification?.data?.operation_id}`
            );
          }
        : null;

    const content = buildToastMessage(notification);

    if (content) {
      const shouldShowPopup = localStorage.getItem('toastNotifications') === 'toastStatus_ON';
      // 1) sempre aggiungo alla notification center (fa aumentare la campanella)

      add({
        id: notification?.id ?? `notif_${Date.now()}`,
        title: notification?.title ?? '',
        content: content,
        date: new Date().toISOString(),
        data: { ...notification?.data, exclude: !shouldShowPopup }
      });

      // 2) solo se la user wants popups
      if (shouldShowPopup) {
        toast.info(content, {
          position: toast.POSITION.BOTTOM_RIGHT,
          toastId: notification?.id,
          ...(isLink && { onClick: isLink }),
        });
      }
      playNotificationSound();
    }
  }

  const onNotifications = (dataArray) => {
    console.log('dataArray', dataArray)
    if (!Array.isArray(dataArray) || dataArray.length === 0) return;
    if (!isLabelsLoaded) return;

    const data = dataArray.filter(
      d => {
        // winery_id can be at top-level OR inside data object
        const wineryId = d?.winery_id || d?.data?.winery_id;
        return Number(wineryId) === Number(activeWinery?.id);
      }
    );

    if (data.length === 0) return;

    let index = 0;

    const processNextNotification = () => {
      if (index >= data.length) return;

      handleNotifications(data[index]);
      index++;

      setTimeout(processNextNotification, 1000);
    };

    processNextNotification();
  };

  const playNotificationSound = () => {
    const audio = new Audio(pop);
    audio.play().catch((e) => console.error("Audio failed", e));
  };

  // Utility to check if two dates are on the same day
  const sameDay = (d1, d2) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const normalizeChatNotesTimestamps = (op) => {
    return {
      ...op,
      chat_notes: op.chat_notes?.map((note) => ({
        ...note,
        timestamp:
          typeof note.timestamp === "string"
            ? note.timestamp
            : note.timestamp?.toISOString?.() ?? note.timestamp,
      })),
    };
  };

  const reloadProtocolApplications = async (axiosPrivate) => {
    try {
      const data = await getProtocolApplications(axiosPrivate);
      dispatch(setApplications(data || []));
    } catch (err) {
      console.warn("Failed to reload protocol applications", err);
    }
  };

  const fetchAndDispatchOperation = async ({
    axiosPrivate,
    operationIds, // generally one, more in case of applied working protocols
    type,
  }) => {
    const ids = Array.isArray(operationIds) ? operationIds : [operationIds];
    // console.log(`🔍 fetchAndDispatchOperation called for type=${type}, ids=${ids}`);

    try {
      for (const operationId of ids) {
        // console.log(`📡 Fetching operation ${operationId}...`);
        const res = await getOperationById(axiosPrivate, operationId);
        // console.log(`📥 Response for op ${operationId}:`, res);

        if (Array.isArray(res) && res.length > 0) {
          let op = res[0];

          if (type === "NOTE") {
            op = normalizeChatNotesTimestamps(op);
          }

          // Se l'operazione è già completata e la notifica è NEW_OPERATION, skippala
          if (type === "NEW_OPERATION" && op.completion_date) {
            continue;
          }

          switch (type) {
            case "NOTE":
              dispatch(updateNote(op));
              break;
            case "NEW_OPERATION":
              console.log('✅ Adding new operation to Redux:', { id: op.id, type: op.type, expire_date: op.expire_date });
              dispatch(addOp(op));
              break;
            case "UPDATE_OPERATION":
            case "START_OPERATION":
            case "COMPLETE_SAMPLING":
              dispatch(updateOp(op));
              // Ricarica protocol applications se è stato completato un sampling
              if (type === "COMPLETE_SAMPLING") {
                reloadProtocolApplications(axiosPrivate);
              }
              break;
            case "DELETE_OPERATION":
            case "COMPLETE_OPERATION":
              if (type !== "DELETE_OPERATION") {
                dispatch(addCompletedOp(op));
              }
              dispatch(removeOp({ id: operationId }));
              // Ricarica protocol applications quando un'operazione è completata
              reloadProtocolApplications(axiosPrivate);
              break;
            case "APPLY_WORKING_PROTOCOL":
            case "APPLY_WORKING_PROTOCOL_POST":
            case "APPLY_FILTERING":
            case "APPLY_STABILIZATION":
              dispatch(addOp(op));
              break;
            default:
              break;
          }
        }
      }
    } catch (err) {
      console.warn("Failed to fetch operation(s) in toast notification", err);
    }
  };
  
  const buildToastMessage = (notification) => {
    const operationId = Number(notification?.data?.operation_id);
    const operationType = notification?.data?.operation_type;

    switch (notification?.type) {
      case "NOTE":
        const opTypeNewNote =
          getLabel(`operationType${notification?.data?.operation_type}`) || "—";
        const opExtendedIdNewNote = notification?.data?.operation_id ? ' #' + notification?.data?.operation_id : "";
        const headingNewNote = getLabel("newNoteFrom");

        if (!isNaN(operationId)) {
          fetchAndDispatchOperation({
            axiosPrivate,
            operationIds: [operationId],
            type: "NOTE",
          });
        }
        
        return `${headingNewNote} ${notification?.from_username} (${opTypeNewNote}${opExtendedIdNewNote})`;

      case "APPLY_WORKING_PROTOCOL":
      case "APPLY_WORKING_PROTOCOL_POST":
      case "APPLY_CIP":
      case "APPLY_SPARKLING":
      case "APPLY_FILTERING":
      case "APPLY_STABILIZATION":
        const operationIds = (notification?.data?.operation_ids || [])?.map(op_id => Number(op_id));
        const date = notification?.data?.start_date ? new Date(notification?.data?.start_date) : null;
        const startDate = date && !isNaN(date) ? format(date, configs.compactDateFormat) : "-";

        let labelKey = "programmingNewWorkingProtocolOp";
        if (notification?.type === "APPLY_WORKING_PROTOCOL_POST") {
          labelKey = "programmingNewPostWorkingProtocolOp";
        } else if (notification?.type === "APPLY_FILTERING") {
          labelKey = "programmingNewFilteringProtocolOp";
        } else if (notification?.type === "APPLY_STABILIZATION") {
          labelKey = "programmingNewStabilizationProtocolOp";
        } else if (notification?.type === "APPLY_SPARKLING") {
          labelKey = "programmingNewSparklingProtocolOp";
        } else if (notification?.type === "APPLY_CIP") {
          labelKey = "programmingNewCIPProtocolOp";
        }

        const heading =
          getLabel(labelKey, { username: notification?.from_username, start_date: startDate }) || "—";

        fetchAndDispatchOperation({
          axiosPrivate,
          operationIds: operationIds,
          type: notification?.type,
        });

        return heading;
  
      case "NEW_OPERATION":
      case "DELETE_OPERATION":
      case "UPDATE_OPERATION":
      case "START_OPERATION":
      case "COMPLETE_SAMPLING":
      case "COMPLETE_OPERATION":
        // console.log(`🎯 Handling ${notification?.type} notification: operationId=${operationId}, operationType=${operationType}`);
        const opTypeNewOp =
          getLabel(`operationType${notification?.data?.operation_type}`) || "—";
        const opExtendedIdNewOp = ' #' + (((operationType === 'ANALYSIS') ? notification?.data?.custom_name : operationId) || "—");
        const headingNewOp = getLabel(
          notification?.type === "NEW_OPERATION"
          ? "programmingNewOp" :
          notification?.type === "COMPLETE_SAMPLING" ?
          "programmingAnalysisSampledOp" :
          // if ANALYSIS, show specific message
          notification?.type === "COMPLETE_OPERATION" ?
           (operationType === 'ANALYSIS' ? "programmingAnalysisCompletedOp" :
          "programmingCompletedOp") :
          notification?.type === "DELETE_OPERATION"
          ? "programmingDeleteOp" :
          notification?.type === "START_OPERATION"
          ? "programmingStartOp"
            : "programmingUpdateOp", { operation: `${opTypeNewOp}${opExtendedIdNewOp}`, username: notification?.from_username || ''}
        );

        if (!isNaN(operationId)) {
          // console.log(`✅ operationId is valid (${operationId}), calling fetchAndDispatchOperation`);
          fetchAndDispatchOperation({
            axiosPrivate,
            operationIds: [operationId],
            type: notification.type,
          });
        } else {
          // console.warn(`❌ operationId is NaN, skipping fetch`);
        }

        return headingNewOp;

      default:
        return null;
    }
  };

  // Daily report logic based on user role
  const isDailyReport = async () => {
    if (role === ROLES.CLIENT) {
      const startTime = new Date();
      startTime.setHours(0, 0, 0, 0);
      const completedOps =
        (await getOperations(axiosPrivate, true, null, startTime)) || [];
      if (completedOps?.length > 0) {
        const customIdDailyReport = "custom-id-daily-report";
        toast(getLabel("toast_dailyReport"), {
          toastId: customIdDailyReport,
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          icon: Icons.success({
            theme: "light",
            type: toast.TYPE.SUCCESS,
          }),
          position: toast.POSITION.BOTTOM_RIGHT,
          data: {
            exclude: false,
          },
        });
      }
    }
  };

  // Update unread counter when notifications change
  useEffect(() => {
    setCounter(unreadCount);
  }, [unreadCount, notifications]);

  // Schedule daily report notifications
  useEffect(() => {
    const endTime = new Date();
    endTime.setHours(19, 54, 30, 0);
    const timeout = endTime - new Date();

    if (role === ROLES.CLIENT && timeout > 0 && sameDay(endTime, new Date()))
      setTimeout(isDailyReport, timeout);
  }, [role]);

  // Toggle unread-only mode
  const toggleDisplayMode = () => {
    dispatch(toggleNotificationsUnreadGrid());
  };

  const readNotification = async (notification_id, isRemove = false) => {
    try {
      const response = await markNotificationAsRead(
        notification_id,
        axiosPrivate
      );

      console.log('response')

      if (response?.success) {
        if (isRemove) {
          remove(notification_id);
        } else {
          markAsRead(notification_id);
        }
      }
    } catch (err) {
      console.log('err', err)
    }
  };

  const deleteAll = async () => {
    // console.log('delete', notifications)
    notifications?.forEach(n => readNotification(n?.id, true));
  }

  const markAllRead = async () => {
    // console.log('delete', notifications)
    notifications?.forEach(n => readNotification(n?.id));
  }

  return (
    <>
      {isOpen && (
        <motion.aside
          onClick={(e) => e.stopPropagation()}
          className="motion-aside-container"
          initial={false}
          variants={variants.container}
          animate={isOpen ? "open" : "closed"}
        >
          <header className="header">
            <h3>{getLabel("notifications")}</h3>
            <div className="unread-filter">
              <Switch
                negative={true}
                leftOption={{
                  label: showUnreadOnly
                    ? getLabel("notificationsShowUnread")
                    : getLabel("notificationsShowAll"),
                }}
                rightOption={{
                  label: "",
                }}
                toggleHandler={toggleDisplayMode}
                status={showUnreadOnly}
              />
            </div>
          </header>
          <AnimatePresence>
            <motion.section
              className="motion-section-content"
              variants={variants.content}
              animate={isOpen ? "open" : "closed"}
            >
              {(!notifications.length ||
                (unreadCount === 0 && showUnreadOnly)) && (
                <motion.h4
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {getLabel("notificationsNoItems")}
                </motion.h4>
              )}
              <AnimatePresence>
                {(showUnreadOnly
                  ? notifications.filter((v) => !v.read)
                  : notifications
                ).map((notification) => (
                  <motion.div
                    key={notification.id}
                    layout
                    initial={{ scale: 0.4, opacity: 0, y: 50 }}
                    exit={{
                      scale: 0,
                      opacity: 0,
                      transition: { duration: 0.2 },
                    }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    style={{ padding: "0.8rem" }}
                  >
                    <motion.article
                      className="motion-article-item"
                      key={notification.id}
                      variants={variants.item}
                    >
                      <div className="icon-wrapper">
                        {notification.icon ||
                          Icons.info({
                            theme: notification.theme || "light",
                            type: toast.TYPE.INFO,
                          })}
                      </div>
                      <div className="content">
                        <div>{notification.content}</div>
                      </div>
                      <div className="tools">
                        {(
                          // (notification?.data?.note_type === "NOTE" ||
                          // notification?.data?.note_type === "NEW_OPERATION") && 
                          notification?.read === true) ? (
                          <Icon                             size={"lg"}
                            icon="fa-check"
                            style={{ color: "#4bbd7f" }}
                          />
                        ) : (
                          <Tooltip
                            variant={"info"}
                            html={getLabel("notificationsMarkAsRead")}
                            place="left"
                            events={["hover"]}
                          >
                            <div
                              className="pulsating-dot"
                              onClick={() => readNotification(notification.id)}
                            >
                              <div className={`circle ${notification.type}`}>
                                <div
                                  className={`ring ${notification.type} `}
                                ></div>
                              </div>
                            </div>
                          </Tooltip>
                        )}
                        <Tooltip
                          variant={"info"}
                          html={getLabel("notificationsMarkAsRemoved")}
                          place="left"
                          events={["hover"]}
                        >
                          <div
                            className="remove"
                            onClick={() =>
                              readNotification(notification.id, true)
                            }
                          >
                            <Icon                               icon="fa-remove"
                              style={{ color: "#bb4456" }}
                              size={"lg"}
                            />
                          </div>
                        </Tooltip>
                      </div>
                    </motion.article>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.section>
          </AnimatePresence>
          <footer className="footer">
            <div className="footer_wrapper">
              <Button
                isRed={true}
                isFull={true}
                label={getLabel("notificationsClearAll")}
                customClassName="sidebar_container_action"
                isNavLink={true}
                disableIcon={true}
                onClick={deleteAll}
              />
              <Button
                isRed={true}
                isFull={true}
                label={getLabel("notificationsMarkAll")}
                customClassName="sidebar_container_action"
                isNavLink={true}
                disableIcon={true}
                onClick={markAllRead}
              />
            </div>
          </footer>
        </motion.aside>
      )}
    </>
  );
};

export default NotificationsCenter;
